import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { showBanner } from 'workflow/common/common.actions'

import { sdkTagsKeys } from 'common/api/queryKeys/sdkTagsKeys'
import Button from 'common/components/button'
import WidgetLoader from 'common/components/loaders/WidgetLoader'
import { WidgetError } from 'common/components/WidgetMessage'
import { formatDate } from 'common/helpers/date'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'

import {
  SdkIdentifiedEvent,
  updateSdkBlockedEvents,
  useGetSdkBlockedEventsQuery,
  useGetSdkConversionEventsQuery,
} from './api'

// Matches the server's normalisation: event names are stored trimmed + lowercased.
const normalizeEventName = (input: string): string => input.trim().toLowerCase()

/** A row in the widget: an identified event and/or a blocklist entry. */
interface ConversionEventView extends SdkIdentifiedEvent {
  blocked: boolean
  seen: boolean
}

// Client-side merge of the two sources: identified events (reporting service,
// from Snowflake) flagged against the blocklist (monorail); blocklisted events
// that were never identified appear as seen: false.
const buildViews = (
  identified: SdkIdentifiedEvent[],
  blocklist: string[]
): ConversionEventView[] => {
  const blockedSet = new Set(blocklist)
  const views: ConversionEventView[] = identified.map((event) => ({
    ...event,
    blocked: blockedSet.has(event.event),
    seen: true,
  }))
  const identifiedNames = new Set(identified.map((e) => e.event))
  const unseen = blocklist
    .filter((event) => !identifiedNames.has(event))
    .map((event) => ({ event, blocked: true, seen: false }))
  return [...views, ...unseen]
}

const BlockedEvents = () => {
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const {
    data: identified,
    isLoading: isEventsLoading,
    isError: isEventsError,
  } = useGetSdkConversionEventsQuery()
  const {
    data: blockedEvents,
    isLoading: isBlockedLoading,
    isError: isBlockedError,
  } = useGetSdkBlockedEventsQuery()

  const [eventInput, setEventInput] = useState('')
  const [eventError, setEventError] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const isLoading = isEventsLoading || isBlockedLoading
  const isError = isEventsError || isBlockedError

  const blocklist = useMemo(() => blockedEvents ?? [], [blockedEvents])
  const views = useMemo(
    () => buildViews(identified ?? [], blocklist),
    [identified, blocklist]
  )

  const persist = async (updated: string[]) => {
    setIsSaving(true)
    try {
      const saved = await updateSdkBlockedEvents({
        accountId,
        blockedEvents: updated,
      })
      // The view recomputes from this cache, so no refetch is needed.
      queryClient.setQueryData<string[]>(
        sdkTagsKeys.blockedEvents(accountId),
        saved
      )
      dispatch(showBanner({ type: 'success', content: 'Blocklist updated' }))
      return true
    } catch {
      dispatch(
        showBanner({
          type: 'error',
          content: 'Failed to update the blocklist. Please try again.',
        })
      )
      return false
    } finally {
      setIsSaving(false)
    }
  }

  const handleToggle = (view: ConversionEventView) => {
    persist(
      view.blocked
        ? blocklist.filter((event) => event !== view.event)
        : [...blocklist, view.event]
    )
  }

  const handleAddEvent = async () => {
    const normalized = normalizeEventName(eventInput)
    if (!normalized) {
      return
    }
    if (blocklist.includes(normalized)) {
      setEventError('This event is already blocked.')
      return
    }
    const ok = await persist([...blocklist, normalized])
    if (ok) {
      setEventInput('')
      setEventError('')
    }
  }

  if (isLoading) {
    return <WidgetLoader />
  }

  return (
    <>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-xl font-medium text-gray-900">Conversion events</h2>
      </div>

      <p className="mb-6 text-sm text-coolGray-600">
        Conversion events seen on this account are listed below (updated daily).
        Blocked events are ignored by Jacquard across every tag on the account.
        Event names are case-insensitive.
      </p>

      {isError && <WidgetError />}

      {!isError && (
        <>
          {views.length > 0 ? (
            <ul
              data-testid="sdk-conversion-event-list"
              className="divide-y divide-coolGray-100 rounded border border-coolGray-200"
            >
              {views.map((view) => (
                <li
                  key={view.event}
                  data-testid="sdk-conversion-event-item"
                  className="flex items-center justify-between px-3 py-2"
                >
                  <div>
                    <span className="font-sourceCodePro text-sm text-coolGray-800">
                      {view.event}
                    </span>
                    <p className="text-xs text-coolGray-400">
                      {view.seen
                        ? `Seen ${view.count ?? 0} times · last ${
                            formatDate(view.last_seen) ?? 'unknown'
                          }`
                        : 'Not seen recently'}
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    {view.blocked && (
                      <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800">
                        Blocked
                      </span>
                    )}
                    <Button
                      data-testid="sdk-conversion-event-toggle"
                      variant="secondary"
                      onClick={() => handleToggle(view)}
                      disabled={isSaving}
                    >
                      {view.blocked ? 'Unblock' : 'Block'}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p
              data-testid="sdk-conversion-event-empty"
              className="text-sm text-coolGray-400 italic"
            >
              No conversion events seen yet — they will appear here once your
              SDK starts sending conversions.
            </p>
          )}

          <p className="mt-6 mb-1 text-sm font-medium text-coolGray-800">
            Block an event before it is seen
          </p>
          <div className="flex gap-2">
            <input
              data-testid="sdk-blocked-event-input"
              type="text"
              value={eventInput}
              onChange={(e) => {
                setEventInput(e.target.value)
                if (eventError) {
                  setEventError('')
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleAddEvent()
                }
              }}
              maxLength={64}
              placeholder="signup"
              disabled={isSaving}
              className="flex-1 rounded border border-coolGray-300 px-3 py-2 text-sm focus:border-maroon-400 focus:outline-none"
            />
            <Button
              data-testid="sdk-blocked-event-add-button"
              variant="secondary"
              onClick={handleAddEvent}
              disabled={isSaving}
            >
              Block event
            </Button>
          </div>
          {eventError && (
            <p className="mt-1 text-sm text-red-500">{eventError}</p>
          )}
        </>
      )}
    </>
  )
}

export default BlockedEvents
